/**
 * PNG encoding, by hand.
 *
 * Not invented-here syndrome — the canvas cannot express what we need to write.
 *
 * A 2D canvas stores pixels premultiplied. `putImageData` with (200,40,40,0)
 * reads back as (0,0,0,0), and `toBlob` encodes from that same buffer, so every
 * colour under a transparent pixel is destroyed on the way out. Measured, not
 * assumed:
 *
 *     wrote     200,40,40,0
 *     read back   0, 0, 0,0
 *     from PNG    0, 0, 0,0
 *
 * That is exactly what the colour-bleed stage produces, and it is the stage that
 * removes the dark halo around every transparent cut-out. Routing the export
 * through a canvas silently threw it away — the pipeline ran, the file did not
 * contain the result, and nothing reported a problem.
 *
 * So the export writes its own bytes. RGBA in, RGBA out, no premultiplication,
 * no browser re-encode. `CompressionStream("deflate")` emits a zlib stream
 * (RFC 1950), which is precisely what an IDAT chunk holds.
 */

const SIGNATURE = Uint8Array.from([137, 80, 78, 71, 13, 10, 26, 10]);

/** RGBA, 8 bits per channel. Roblox accepts this and Roblox is the only reader
 *  that matters — greyscale and palette modes would just be untested paths. */
const COLOUR_TYPE_RGBA = 6;
const BIT_DEPTH = 8;
const BYTES_PER_PIXEL = 4;

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    c = (CRC_TABLE[(c ^ (bytes[i] as number)) & 0xff] as number) ^ (c >>> 8);
  }
  return (c ^ 0xffffffff) >>> 0;
}

/** length · type · data · CRC(type+data). The CRC deliberately excludes the
 *  length field — a detail that produces a file every decoder rejects. */
function chunk(type: string, data: Uint8Array): Uint8Array {
  const out = new Uint8Array(12 + data.length);
  const view = new DataView(out.buffer);
  view.setUint32(0, data.length);
  for (let i = 0; i < 4; i++) out[4 + i] = type.charCodeAt(i);
  out.set(data, 8);
  view.setUint32(8 + data.length, crc32(out.subarray(4, 8 + data.length)));
  return out;
}

/**
 * Choose a per-row filter.
 *
 * PNG lets every scanline pick its own predictor, and the choice is worth making
 * rather than always writing 0: a 585x559 RGBA image is 1.3 MB raw, and export
 * refuses anything over 1 MB. None/Sub/Up is the cheap subset — Average and
 * Paeth cost more arithmetic than they save on flat garment artwork.
 *
 * The heuristic is the one from the PNG spec: pick the filter whose output has
 * the smallest sum of absolute signed values, on the theory that bytes near zero
 * compress best.
 */
function filterRow(
  raw: Uint8Array,
  prior: Uint8Array | null,
  stride: number,
  out: Uint8Array,
  outOffset: number,
) {
  const none = raw;

  const sub = new Uint8Array(stride);
  for (let i = 0; i < stride; i++) {
    const left = i >= BYTES_PER_PIXEL ? (raw[i - BYTES_PER_PIXEL] as number) : 0;
    sub[i] = ((raw[i] as number) - left) & 0xff;
  }

  const up = new Uint8Array(stride);
  for (let i = 0; i < stride; i++) {
    up[i] = ((raw[i] as number) - (prior ? (prior[i] as number) : 0)) & 0xff;
  }

  // Signed magnitude: 0xFF is -1, i.e. cheap, not expensive.
  const cost = (bytes: Uint8Array) => {
    let sum = 0;
    for (let i = 0; i < bytes.length; i++) {
      const v = bytes[i] as number;
      sum += v < 128 ? v : 256 - v;
    }
    return sum;
  };

  let type = 0;
  let best = none;
  let bestCost = cost(none);
  const subCost = cost(sub);
  if (subCost < bestCost) {
    type = 1;
    best = sub;
    bestCost = subCost;
  }
  if (cost(up) < bestCost) {
    type = 2;
    best = up;
  }

  out[outOffset] = type;
  out.set(best, outOffset + 1);
}

async function deflate(bytes: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([bytes as BlobPart])
    .stream()
    .pipeThrough(new CompressionStream("deflate"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/**
 * Encode RGBA bytes as a PNG.
 *
 * `data` is consumed exactly as given: no premultiplication, no colour
 * management, no alpha-zero flattening. What the pipeline wrote is what lands in
 * the file.
 */
export async function encodePng(
  data: Uint8ClampedArray,
  width: number,
  height: number,
): Promise<Uint8Array> {
  const stride = width * BYTES_PER_PIXEL;
  if (data.length !== stride * height) {
    throw new Error(
      `Expected ${stride * height} bytes for ${width}x${height}, got ${data.length}.`,
    );
  }

  const ihdr = new Uint8Array(13);
  const header = new DataView(ihdr.buffer);
  header.setUint32(0, width);
  header.setUint32(4, height);
  ihdr[8] = BIT_DEPTH;
  ihdr[9] = COLOUR_TYPE_RGBA;
  ihdr[10] = 0; // compression: deflate, the only defined value
  ihdr[11] = 0; // filter method: adaptive, the only defined value
  ihdr[12] = 0; // interlace: none

  // One extra byte per row for the filter type.
  const filtered = new Uint8Array((stride + 1) * height);
  let prior: Uint8Array | null = null;
  for (let y = 0; y < height; y++) {
    const raw = new Uint8Array(data.buffer, data.byteOffset + y * stride, stride);
    filterRow(raw, prior, stride, filtered, y * (stride + 1));
    prior = raw;
  }

  const idat = await deflate(filtered);

  const parts = [
    SIGNATURE,
    chunk("IHDR", ihdr),
    chunk("IDAT", idat),
    chunk("IEND", new Uint8Array(0)),
  ];
  const total = parts.reduce((n, p) => n + p.length, 0);
  const png = new Uint8Array(total);
  let at = 0;
  for (const p of parts) {
    png.set(p, at);
    at += p.length;
  }
  return png;
}
